import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Store, Mail, User, MessageSquare, Loader2, CheckCircle, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const recommendSchema = z.object({
  customerName: z.string().trim().min(1, "Your name is required").max(100),
  customerEmail: z.string().trim().email("Please enter a valid email").max(255),
  storeName: z.string().trim().min(1, "Store name is required").max(200),
  storeWebsite: z.string().trim().max(500).optional(),
  reason: z.string().trim().min(10, "Please tell us why you'd recommend us (at least 10 characters)").max(1000),
});

type RecommendFormData = z.infer<typeof recommendSchema>;

interface RecommendStoreDialogProps {
  children: React.ReactNode;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function RecommendStoreDialog({ children }: RecommendStoreDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<RecommendFormData>({
    customerName: "",
    customerEmail: "",
    storeName: "",
    storeWebsite: "",
    reason: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof RecommendFormData, string>>>({});

  const handleChange = (field: keyof RecommendFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = recommendSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof RecommendFormData, string>> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof RecommendFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: formData.customerName,
          email: formData.customerEmail,
          subject: `Store Recommendation: ${formData.storeName}`,
          message: `A customer wants to recommend Shakatah to a store!\n\n` +
            `Customer: ${formData.customerName} (${formData.customerEmail})\n\n` +
            `Store Name: ${formData.storeName}\n` +
            `Store Website: ${formData.storeWebsite || "Not provided"}\n\n` +
            `Why they recommend us:\n${formData.reason}`,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Thank you! 💜",
        description: "We'll reach out to the store and let them know about Shakatah.",
      });
    } catch (err: any) {
      console.error("Error submitting recommendation:", err);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: "",
      customerEmail: "",
      storeName: "",
      storeWebsite: "",
      reason: "",
    });
    setErrors({});
    setIsSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) {
        setTimeout(resetForm, 300);
      }
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-customer" />
            Recommend Us to a Store
          </DialogTitle>
          <DialogDescription>
            Know a store that should offer group gifting? Tell us about them!
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-customer/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-customer" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Thanks for spreading the love! 💜
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              We'll reach out to {formData.storeName} and mention you recommended us.
            </p>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <User className="w-4 h-4" />
                Your Details
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="customerName" className="text-xs">Your Name *</Label>
                  <Input
                    id="customerName"
                    placeholder="John Doe"
                    value={formData.customerName}
                    onChange={(e) => handleChange("customerName", e.target.value)}
                    className={errors.customerName ? "border-destructive" : ""}
                  />
                  {errors.customerName && (
                    <p className="text-xs text-destructive">{errors.customerName}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="customerEmail" className="text-xs">Your Email *</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    placeholder="you@email.com"
                    value={formData.customerEmail}
                    onChange={(e) => handleChange("customerEmail", e.target.value)}
                    className={errors.customerEmail ? "border-destructive" : ""}
                  />
                  {errors.customerEmail && (
                    <p className="text-xs text-destructive">{errors.customerEmail}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Store Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Store className="w-4 h-4" />
                Store Details
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="storeName" className="text-xs">Store Name *</Label>
                  <Input
                    id="storeName"
                    placeholder="e.g., The Gift Shop"
                    value={formData.storeName}
                    onChange={(e) => handleChange("storeName", e.target.value)}
                    className={errors.storeName ? "border-destructive" : ""}
                  />
                  {errors.storeName && (
                    <p className="text-xs text-destructive">{errors.storeName}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="storeWebsite" className="text-xs">Store Website (optional)</Label>
                  <Input
                    id="storeWebsite"
                    placeholder="e.g., www.thegiftshop.com"
                    value={formData.storeWebsite}
                    onChange={(e) => handleChange("storeWebsite", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1.5">
                <MessageSquare className="w-4 h-4" />
                Why would you recommend us? *
              </div>
              <Textarea
                id="reason"
                placeholder="Tell us why this store would be great for group gifting..."
                value={formData.reason}
                onChange={(e) => handleChange("reason", e.target.value)}
                className={errors.reason ? "border-destructive" : ""}
                rows={3}
              />
              {errors.reason && (
                <p className="text-xs text-destructive">{errors.reason}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-customer hover:bg-customer/90 text-customer-foreground"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Submit Recommendation
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
